import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { SignaturePad } from "@/components/SignaturePad";
import { CartrackLiveMap } from "@/components/CartrackLiveMap";
import { MapPin, Settings, Plug, RefreshCw, Truck, Map as MapIcon, Route, Hexagon, ListChecks, ExternalLink, AlertCircle, FileSignature, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "vehicles", label: "Vehicles", icon: Truck },
  { id: "live", label: "Live map", icon: MapIcon },
  { id: "licences", label: "Licences & RUC", icon: FileSignature },
  { id: "trips", label: "Trips", icon: Route },
  { id: "geofences", label: "Geofences", icon: Hexagon },
  { id: "events", label: "Events", icon: ListChecks },
];

export default function LiveTracking() {
  const { user } = useAuth();
  const [cartrack, setCartrack] = useState(null);
  const [eroad, setEroad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("vehicles");
  const [ctOpen, setCtOpen] = useState(false);
  const [erOpen, setErOpen] = useState(false);

  const isOffice = user.role !== "field";

  const loadSettings = async () => {
    try {
      const [ct, er] = await Promise.all([
        api.get("/cartrack/settings"),
        api.get("/eroad/settings"),
      ]);
      setCartrack(ct.data);
      setEroad(er.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  if (!isOffice) {
    return (
      <div className="space-y-6">
        <EmptyState icon={MapPin} title="Office only" subtitle="Live tracking is managed by the office." />
      </div>
    );
  }

  const provider = cartrack?.connected ? "cartrack" : eroad?.connected ? "eroad" : null;
  // EROAD's public API only gives vehicles + live positions + odometer — trips,
  // geofences and events are Cartrack-only, so hide them when EROAD is active.
  const visibleTabs = provider === "eroad" ? TABS.filter((t) => ["vehicles", "live", "licences"].includes(t.id)) : TABS;
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : "vehicles";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="/ Fleet on a map"
        title="Live Tracking"
        subtitle="Vehicles, trips and geofences from your telematics provider, mirrored into Fieldlink."
        action={
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={loadSettings}
              className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold"
              data-testid="ct-refresh-settings"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            {provider !== "eroad" && (
              <Dialog open={ctOpen} onOpenChange={setCtOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold"
                    data-testid="ct-open-settings"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {cartrack?.connected ? "Manage Cartrack" : "Connect Cartrack"}
                  </Button>
                </DialogTrigger>
                <CartrackSettingsDialog
                  settings={cartrack}
                  onSaved={() => { setCtOpen(false); loadSettings(); }}
                  onDisconnect={() => { setCtOpen(false); loadSettings(); }}
                />
              </Dialog>
            )}
            {provider !== "cartrack" && (
              <Dialog open={erOpen} onOpenChange={setErOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant={eroad?.connected ? "default" : "outline"}
                    className={eroad?.connected ? "rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" : "rounded-sm border-zinc-900 uppercase tracking-wider font-bold"}
                    data-testid="er-open-settings"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {eroad?.connected ? "Manage EROAD" : "Connect EROAD"}
                  </Button>
                </DialogTrigger>
                <EroadSettingsDialog
                  settings={eroad}
                  onSaved={() => { setErOpen(false); loadSettings(); }}
                  onDisconnect={() => { setErOpen(false); loadSettings(); }}
                />
              </Dialog>
            )}
          </div>
        }
      />

      {loading ? null : !provider ? (
        <NotConnectedCard onConnectCartrack={() => setCtOpen(true)} onConnectEroad={() => setErOpen(true)} />
      ) : (
        <>
          {provider === "cartrack" ? <ConnectionStatus settings={cartrack} /> : <EroadConnectionStatus settings={eroad} />}
          <TabsBar tabs={visibleTabs} tab={activeTab} onChange={setTab} />
          {activeTab === "vehicles" && (provider === "cartrack" ? <VehiclesTab /> : <EroadVehiclesTab />)}
          {activeTab === "live" && <CartrackLiveMap provider={provider} />}
          {activeTab === "licences" && <LicencesTab />}
          {activeTab === "trips" && <TripsTab />}
          {activeTab === "geofences" && <GeofencesTab />}
          {activeTab === "events" && <EventsTab />}
        </>
      )}
    </div>
  );
}

function NotConnectedCard({ onConnectCartrack, onConnectEroad }) {
  return (
    <div className="border-2 border-zinc-900 dark:border-zinc-700 bg-white dark:bg-[#151C27] p-6" data-testid="ct-not-connected">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF5F15]">No tracking provider connected</div>
      <h3 className="text-xl font-display font-black uppercase tracking-tight mt-1">Connect your fleet tracking</h3>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-2 leading-relaxed">
        Fieldlink uses <span className="font-bold">one tracking provider per organisation</span>. Pick the one your fleet runs on — vehicle positions land on the same live map either way.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 mt-4">
        <div className="border border-zinc-300 dark:border-zinc-700 p-4 flex flex-col">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#FF5F15] text-white flex items-center justify-center shrink-0"><Plug className="w-5 h-5" /></div>
            <div className="font-display font-black uppercase tracking-tight">Cartrack</div>
          </div>
          <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-2 leading-relaxed flex-1">
            Generate an API user in <a href="https://fleetweb-nz.cartrack.com" target="_blank" rel="noreferrer" className="underline text-[#FF5F15]">Fleetweb</a> (Settings → API Settings) and paste the username + password.
          </p>
          <Button onClick={onConnectCartrack} className="mt-3 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="ct-connect-cta">
            <Plug className="w-4 h-4 mr-2" /> Connect Cartrack
          </Button>
        </div>
        <div className="border border-zinc-300 dark:border-zinc-700 p-4 flex flex-col">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center shrink-0"><Plug className="w-5 h-5" /></div>
            <div className="font-display font-black uppercase tracking-tight">EROAD</div>
          </div>
          <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-2 leading-relaxed flex-1">
            Have your EROAD admin create an API key in <span className="font-mono text-[11px]">MyEROAD → Administration → Integrations → API Keys</span>, then paste it here.
          </p>
          <Button onClick={onConnectEroad} variant="outline" className="mt-3 rounded-sm border-zinc-900 uppercase tracking-wider font-bold" data-testid="er-connect-cta">
            <Plug className="w-4 h-4 mr-2" /> Connect EROAD
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConnectionStatus({ settings }) {
  const ok = settings.last_test_status === "success";
  return (
    <div className={`border-2 ${ok ? "border-green-600" : "border-yellow-500"} bg-white dark:bg-[#151C27] p-3 flex items-center gap-3 flex-wrap`} data-testid="ct-status">
      <div className={`w-8 h-8 ${ok ? "bg-green-600" : "bg-yellow-500"} text-white flex items-center justify-center shrink-0`}>
        <Plug className="w-4 h-4" />
      </div>
      <div className="text-xs flex-1 min-w-0">
        <div className="font-bold uppercase tracking-wider">
          Connected as <span className="font-mono">{settings.username}</span> · region <span className="font-mono uppercase">{settings.region}</span>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
          Last test {ok ? "OK" : settings.last_test_status || "pending"}
          {settings.last_test_at ? ` · ${new Date(settings.last_test_at).toLocaleString()}` : ""}
        </div>
      </div>
      <a href="https://fleetweb-nz.cartrack.com" target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase tracking-widest text-[#FF5F15] hover:underline inline-flex items-center gap-1">
        Open Fleetweb <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function EroadConnectionStatus({ settings }) {
  const ok = settings.last_test_status === "success";
  return (
    <div className={`border-2 ${ok ? "border-green-600" : "border-yellow-500"} bg-white dark:bg-[#151C27] p-3 flex items-center gap-3 flex-wrap`} data-testid="er-status">
      <div className={`w-8 h-8 ${ok ? "bg-green-600" : "bg-yellow-500"} text-white flex items-center justify-center shrink-0`}>
        <Plug className="w-4 h-4" />
      </div>
      <div className="text-xs flex-1 min-w-0">
        <div className="font-bold uppercase tracking-wider">EROAD connected · region <span className="font-mono uppercase">{settings.region}</span></div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
          Last test {ok ? "OK" : settings.last_test_status || "pending"}
          {settings.last_test_at ? ` · ${new Date(settings.last_test_at).toLocaleString()}` : ""}
        </div>
      </div>
      <a href="https://my.eroad.com" target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase tracking-widest text-[#FF5F15] hover:underline inline-flex items-center gap-1">
        Open MyEROAD <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function EroadSettingsDialog({ settings, onSaved, onDisconnect }) {
  const [apiKey, setApiKey] = useState("");
  const [region, setRegion] = useState(settings?.region || "nz");
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);

  const handleTest = async () => {
    if (!apiKey && !settings?.connected) { toast.error("Paste the EROAD API key first."); return; }
    setBusy(true); setTest(null);
    try {
      const { data } = await api.post("/eroad/test-connection", { api_key: apiKey || null, region });
      setTest({ ok: true, msg: `Connected. EROAD returned ${data.sample_count ?? "?"} sample vehicle(s).` });
    } catch (e) {
      setTest({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const handleSave = async () => {
    if (!apiKey) { toast.error("The API key is required to save."); return; }
    setBusy(true);
    try {
      await api.post("/eroad/settings", { api_key: apiKey, region });
      toast.success("EROAD connected.");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect EROAD? You can reconnect anytime by pasting the API key again.")) return;
    setBusy(true);
    try {
      await api.delete("/eroad/settings");
      toast.success("EROAD disconnected.");
      onDisconnect?.();
    } finally { setBusy(false); }
  };

  const regions = settings?.regions || ["nz", "au", "na"];
  const hostFor = (r) => ({ nz: "api.apac.eroad.com", au: "api.au.eroad.com", na: "api.na.eroad.com" }[r] || "api.apac.eroad.com");

  return (
    <DialogContent className="max-w-lg" data-testid="er-settings-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
          <Plug className="w-5 h-5 text-[#FF5F15]" /> EROAD connection
        </DialogTitle>
        <DialogDescription>
          Office-only. EROAD has no &quot;sign in&quot; for apps — a Client Administrator generates an API key in{" "}
          <span className="font-mono text-xs">MyEROAD → Administration → Integrations → API Keys</span> (needs a plan with API access), then pastes it here. We store it encrypted at rest; the browser never sees it again.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="label-tag">EROAD API key</Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="mt-2 rounded-sm border-zinc-400 font-mono"
            placeholder={settings?.connected ? "Leave blank to keep existing key" : "Paste the generated API key"}
            data-testid="er-apikey-input"
          />
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Stored encrypted (Fernet). Sent only to EROAD, never to the browser.</p>
        </div>
        <div>
          <Label className="label-tag">Region</Label>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="er-region-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r} value={r} data-testid={`er-region-${r}`}>{r.toUpperCase()} — {hostFor(r)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {test && (
          <div className={`border-2 ${test.ok ? "border-green-600 bg-green-50 dark:bg-green-900/10" : "border-red-600 bg-red-50 dark:bg-red-900/10"} p-3 text-xs`} data-testid="er-test-result">
            <div className="flex items-start gap-2">
              {test.ok ? <Plug className="w-4 h-4 text-green-700 dark:text-green-400 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-red-700 dark:text-red-400 shrink-0 mt-0.5" />}
              <div className="min-w-0 break-words">{test.msg}</div>
            </div>
          </div>
        )}
      </div>
      <DialogFooter className="gap-2 flex-col sm:flex-row sm:justify-between">
        <div className="flex gap-2">
          {settings?.connected && (
            <Button type="button" variant="outline" onClick={handleDisconnect} disabled={busy} className="rounded-sm border-red-600 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 uppercase tracking-wider font-bold" data-testid="er-disconnect">
              Disconnect
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleTest} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold" data-testid="er-test-btn">
            {busy ? "Testing…" : "Test"}
          </Button>
          <Button type="button" onClick={handleSave} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="er-save-btn">
            {busy ? "Saving…" : "Save & connect"}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
}

function EroadVehiclesTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get("/tracking/vehicles/status");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  if (err) return <CartrackError err={err} />;
  return (
    <div className="space-y-3" data-testid="er-vehicles-tab">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="er-vehicles-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={Truck} title={busy ? "Loading…" : "No vehicles"} subtitle="EROAD returned no vehicles for this account yet." />
      ) : (
        <div className="overflow-x-auto border-2 border-zinc-900 dark:border-zinc-700">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-white">
              <tr className="text-left text-[10px] uppercase tracking-widest">
                <th className="px-3 py-2">Rego</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Speed</th>
                <th className="px-3 py-2">Odometer (km)</th>
                <th className="px-3 py-2">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.vehicle_id || r.registration || i} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`er-vehicle-row-${i}`}>
                  <td className="px-3 py-2 font-mono font-bold">{r.registration || "—"}</td>
                  <td className="px-3 py-2">{r.status || "—"}</td>
                  <td className="px-3 py-2">{r.speed != null ? `${fmtNum(r.speed)} km/h` : "—"}</td>
                  <td className="px-3 py-2">{fmtNum(r.odometer)}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{fmtDate(r.location?.updated || r.event_ts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Odometer readings come straight from EROAD · live positions are on the Live map tab</p>
    </div>
  );
}


function TabsBar({ tabs = TABS, tab, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 border-b-2 border-zinc-200 dark:border-zinc-800" data-testid="ct-tabs">
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-[2px] ${
              active ? "border-[#FF5F15] text-[#FF5F15]" : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
            }`}
            data-testid={`ct-tab-${t.id}`}
          >
            <Icon className="w-4 h-4" /> {t.label}
          </button>
        );
      })}
    </div>
  );
}

function CartrackSettingsDialog({ settings, onSaved, onDisconnect }) {
  const [username, setUsername] = useState(settings?.username || "");
  const [password, setPassword] = useState("");
  const [region, setRegion] = useState(settings?.region || "nz");
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);  // {ok, msg}

  const handleTest = async () => {
    if (!username || (!password && !settings?.connected)) {
      toast.error("Enter the username and password first.");
      return;
    }
    setBusy(true); setTest(null);
    try {
      const { data } = await api.post("/cartrack/test-connection", { username, password: password || null, region });
      setTest({ ok: true, msg: `Connected. Cartrack returned ${data.sample_count ?? "?"} sample vehicle(s).` });
    } catch (e) {
      setTest({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const handleSave = async () => {
    if (!username || !password) {
      toast.error("Both username and password are required to save.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/cartrack/settings", { username, password, region });
      toast.success("Cartrack connected.");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Cartrack? You can reconnect anytime by pasting credentials again.")) return;
    setBusy(true);
    try {
      await api.delete("/cartrack/settings");
      toast.success("Cartrack disconnected.");
      onDisconnect?.();
    } finally { setBusy(false); }
  };

  const regions = settings?.regions || ["nz", "au", "za"];

  return (
    <DialogContent className="max-w-lg" data-testid="ct-settings-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
          <Plug className="w-5 h-5 text-[#FF5F15]" /> Cartrack connection
        </DialogTitle>
        <DialogDescription>
          Office-only. Generate an API user inside Cartrack Fleetweb at{" "}
          <span className="font-mono text-xs">Settings → API Settings → Generate User Credentials</span>, then paste the credentials here. We store the password encrypted at rest; the browser never sees it after this dialog.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="label-tag">Cartrack username</Label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} className="mt-2 rounded-sm border-zinc-400 font-mono" placeholder="api_user_xyz" data-testid="ct-username-input" />
        </div>
        <div>
          <Label className="label-tag">API password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 rounded-sm border-zinc-400 font-mono"
            placeholder={settings?.connected ? "Leave blank to keep existing password" : "Paste the generated password"}
            data-testid="ct-password-input"
          />
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Stored encrypted (Fernet, AES-128-CBC + HMAC).</p>
        </div>
        <div>
          <Label className="label-tag">Region</Label>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="ct-region-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r} value={r} data-testid={`ct-region-${r}`}>{r.toUpperCase()} — fleetapi-{r}.cartrack.com</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {test && (
          <div
            className={`border-2 ${test.ok ? "border-green-600 bg-green-50 dark:bg-green-900/10" : "border-red-600 bg-red-50 dark:bg-red-900/10"} p-3 text-xs`}
            data-testid="ct-test-result"
          >
            <div className="flex items-start gap-2">
              {test.ok ? <Plug className="w-4 h-4 text-green-700 dark:text-green-400 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-red-700 dark:text-red-400 shrink-0 mt-0.5" />}
              <div className="min-w-0 break-words">{test.msg}</div>
            </div>
          </div>
        )}
      </div>
      <DialogFooter className="gap-2 flex-col sm:flex-row sm:justify-between">
        <div className="flex gap-2">
          {settings?.connected && (
            <Button type="button" variant="outline" onClick={handleDisconnect} disabled={busy} className="rounded-sm border-red-600 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 uppercase tracking-wider font-bold" data-testid="ct-disconnect">
              Disconnect
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleTest} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold" data-testid="ct-test-btn">
            {busy ? "Testing…" : "Test"}
          </Button>
          <Button type="button" onClick={handleSave} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="ct-save-btn">
            {busy ? "Saving…" : "Save & connect"}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
}

// --- helpers -------------------------------------------------------------
function unwrapList(data) {
  // Cartrack responses can be {data:[...]} or a plain array
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}
function fmtNum(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
}
function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

function CartrackError({ err }) {
  return (
    <div className="border-2 border-red-600 bg-red-50 dark:bg-red-900/10 p-4" data-testid="ct-error">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-400 shrink-0 mt-0.5" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 dark:text-red-400">Cartrack call failed</div>
          <div className="text-sm font-bold mt-0.5 break-words">{err?.response?.data?.detail || err?.message || "Unknown error"}</div>
        </div>
      </div>
    </div>
  );
}

// --- tabs ----------------------------------------------------------------
function VehiclesTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get("/cartrack/vehicles");
      setRows(unwrapList(data));
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  if (err) return <CartrackError err={err} />;
  return (
    <div data-testid="ct-vehicles-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{rows.length} vehicle{rows.length === 1 ? "" : "s"}</div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold text-xs">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={Truck} title="No vehicles" subtitle="Cartrack returned an empty list." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((v, i) => (
            <div key={v.id || v.registration || i} className="border-2 border-zinc-300 dark:border-zinc-700 bg-white dark:bg-[#151C27] p-3" data-testid={`ct-vehicle-${i}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono text-[10px] font-bold uppercase tracking-widest bg-[#09090B] text-white px-1.5 py-0.5">{v.registration || v.rego || v.id}</span>
                <span className="font-bold text-sm leading-tight break-words">{v.description || v.make || ""} {v.model || ""}</span>
              </div>
              {v.driver_name && <div className="text-xs text-zinc-500 mt-1">Driver: {v.driver_name}</div>}
              {v.last_update && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Last: {fmtDate(v.last_update)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveStatusTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get("/cartrack/vehicles/status");
      setRows(unwrapList(data));
    } catch (e) { setErr(e); if (e.response?.status === 429) setPaused(true); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    load();
    if (paused) return;
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line
  }, [paused]);
  if (err && !rows.length) return <CartrackError err={err} />;
  return (
    <div data-testid="ct-live-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {rows.length} vehicle{rows.length === 1 ? "" : "s"} · {paused ? "Paused" : "Polling every 15s"}
        </div>
        <Button size="sm" variant="outline" onClick={() => setPaused((p) => !p)} className="rounded-sm uppercase tracking-wider font-bold text-xs">
          {paused ? "Resume" : "Pause"}
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={MapIcon} title="No live data yet" subtitle="Cartrack hasn't returned any vehicle status." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((v, i) => {
            const lat = v.latitude ?? v.lat;
            const lng = v.longitude ?? v.lng ?? v.lon;
            const mapsUrl = (lat && lng) ? `https://www.google.com/maps?q=${lat},${lng}` : null;
            return (
              <div key={v.id || v.registration || i} className="border-2 border-zinc-300 dark:border-zinc-700 bg-white dark:bg-[#151C27] p-3" data-testid={`ct-live-${i}`}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest bg-[#09090B] text-white px-1.5 py-0.5">{v.registration || v.rego || v.id}</span>
                  <span className="font-bold text-sm leading-tight break-words">{v.description || ""}</span>
                </div>
                <div className="text-xs text-zinc-600 dark:text-zinc-300 mt-2 space-y-0.5">
                  {v.speed != null && <div>Speed: <span className="font-bold">{fmtNum(v.speed)} km/h</span></div>}
                  {v.odometer != null && <div>Odometer: <span className="font-bold">{fmtNum(v.odometer)} km</span></div>}
                  {v.fuel_level != null && <div>Fuel: <span className="font-bold">{fmtNum(v.fuel_level)}%</span></div>}
                  {v.address && <div className="break-words">📍 {v.address}</div>}
                  {v.event_ts && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">As at {fmtDate(v.event_ts)}</div>}
                </div>
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold uppercase tracking-widest text-[#FF5F15] hover:underline">
                    Open in Maps <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TripsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [rego, setRego] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const params = { start_date: `${start} 00:00:00`, end_date: `${end} 23:59:59`, limit: 200 };
      if (rego) params.registration = rego;
      const { data } = await api.get("/cartrack/trips", { params });
      setRows(unwrapList(data));
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div data-testid="ct-trips-tab" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
        <div>
          <Label className="label-tag">From</Label>
          <DateField value={start} onChange={setStart} className="mt-2" testId="ct-trips-start" />
        </div>
        <div>
          <Label className="label-tag">To</Label>
          <DateField value={end} onChange={setEnd} className="mt-2" testId="ct-trips-end" />
        </div>
        <div>
          <Label className="label-tag">Rego (optional)</Label>
          <Input value={rego} onChange={(e) => setRego(e.target.value)} className="mt-2 rounded-sm border-zinc-400 font-mono" placeholder="ABC123" data-testid="ct-trips-rego" />
        </div>
        <Button onClick={load} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold">
          {busy ? "Loading…" : "Load trips"}
        </Button>
      </div>
      {err ? <CartrackError err={err} /> : rows.length === 0 ? (
        <EmptyState icon={Route} title="No trips" subtitle="No trips for the selected window." />
      ) : (
        <div className="border-2 border-zinc-300 dark:border-zinc-700 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
              <tr>
                <th className="text-left p-2 font-bold uppercase tracking-wider">Rego</th>
                <th className="text-left p-2 font-bold uppercase tracking-wider">Start</th>
                <th className="text-left p-2 font-bold uppercase tracking-wider">End</th>
                <th className="text-right p-2 font-bold uppercase tracking-wider">Km</th>
                <th className="text-right p-2 font-bold uppercase tracking-wider">Max km/h</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => (
                <tr key={t.id || i} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`ct-trip-${i}`}>
                  <td className="p-2 font-mono">{t.registration || t.rego || "—"}</td>
                  <td className="p-2">{fmtDate(t.start_time || t.start_date)}</td>
                  <td className="p-2">{fmtDate(t.end_time || t.end_date)}</td>
                  <td className="p-2 text-right font-bold">{fmtNum(t.distance || t.km || t.total_distance)}</td>
                  <td className="p-2 text-right">{fmtNum(t.max_speed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GeofencesTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get("/cartrack/geofences");
      setRows(unwrapList(data));
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  if (err) return <CartrackError err={err} />;
  return (
    <div data-testid="ct-geofences-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{rows.length} geofence{rows.length === 1 ? "" : "s"}</div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold text-xs">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={Hexagon} title="No geofences" subtitle="Create geofences in Cartrack Fleetweb to see them here." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((g, i) => (
            <div key={g.id || i} className="border-2 border-zinc-300 dark:border-zinc-700 bg-white dark:bg-[#151C27] p-3" data-testid={`ct-geofence-${i}`}>
              <div className="font-bold text-sm leading-tight break-words">{g.name || g.title || `Geofence ${i + 1}`}</div>
              {g.address && <div className="text-xs text-zinc-500 mt-1 break-words">{g.address}</div>}
              {g.radius && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Radius {fmtNum(g.radius)} m</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [rego, setRego] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    if (!rego) { toast.error("Enter a vehicle rego to load events."); return; }
    setBusy(true); setErr(null);
    try {
      const params = {
        registration: rego,
        start_date: `${start} 00:00:00`,
        end_date: `${end} 23:59:59`,
        limit: 300,
      };
      const { data } = await api.get("/cartrack/events", { params });
      setRows(unwrapList(data));
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="ct-events-tab" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
        <div>
          <Label className="label-tag">Rego</Label>
          <Input value={rego} onChange={(e) => setRego(e.target.value)} className="mt-2 rounded-sm border-zinc-400 font-mono" placeholder="ABC123" data-testid="ct-events-rego" required />
        </div>
        <div>
          <Label className="label-tag">From</Label>
          <DateField value={start} onChange={setStart} className="mt-2" testId="ct-events-start" />
        </div>
        <div>
          <Label className="label-tag">To</Label>
          <DateField value={end} onChange={setEnd} className="mt-2" testId="ct-events-end" />
        </div>
        <Button onClick={load} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold">
          {busy ? "Loading…" : "Load events"}
        </Button>
      </div>
      {err ? <CartrackError err={err} /> : rows.length === 0 ? (
        <EmptyState icon={ListChecks} title="No events" subtitle="Pick a rego and date range, then load." />
      ) : (
        <div className="border-2 border-zinc-300 dark:border-zinc-700 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
              <tr>
                <th className="text-left p-2 font-bold uppercase tracking-wider">When</th>
                <th className="text-left p-2 font-bold uppercase tracking-wider">Event</th>
                <th className="text-right p-2 font-bold uppercase tracking-wider">Speed</th>
                <th className="text-left p-2 font-bold uppercase tracking-wider">Where</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((ev, i) => (
                <tr key={ev.id || i} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`ct-event-${i}`}>
                  <td className="p-2 whitespace-nowrap">{fmtDate(ev.event_ts || ev.timestamp || ev.time)}</td>
                  <td className="p-2 break-words">{ev.event_type || ev.event || ev.type || "—"}</td>
                  <td className="p-2 text-right">{fmtNum(ev.speed)}</td>
                  <td className="p-2 break-words">{ev.address || (ev.latitude ? `${ev.latitude}, ${ev.longitude}` : "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// --- Licences & RUC tab -------------------------------------------------
function daysUntil(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d - today) / (24 * 60 * 60 * 1000));
  } catch { return null; }
}

function LicencesTab() {
  const [balances, setBalances] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [consent, setConsent] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const [bal, pur, cons] = await Promise.all([
        api.get("/ruc/balances"),
        api.get("/ruc/purchases"),
        api.get("/ruc/consent"),
      ]);
      setBalances(bal.data || []);
      setPurchases(pur.data || []);
      setConsent(cons.data);
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const buckets = useMemo(() => {
    const out = { expired: 0, low: 0, warn: 0, ok: 0, unknown: 0 };
    balances.forEach((b) => { out[b.status] = (out[b.status] || 0) + 1; });
    return out;
  }, [balances]);

  const openNzta = async (registration, km) => {
    try {
      const { data } = await api.get("/ruc/nzta-deep-link", { params: { registration, km } });
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open NZTA portal");
    }
  };

  if (err) return <CartrackError err={err} />;

  return (
    <div data-testid="ct-licences-tab" className="space-y-5">
      {/* Consent banner */}
      {!consent?.signed ? (
        <div className="border-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10 p-4" data-testid="ruc-no-consent">
          <div className="flex items-start gap-3 flex-wrap">
            <ShieldCheck className="w-5 h-5 text-yellow-700 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-yellow-700 dark:text-yellow-400">Auto-buy RUC — consent required</div>
              <div className="text-sm font-bold mt-0.5">Sign the consent terms so Fieldlink can buy RUC on your behalf when a reseller is wired.</div>
            </div>
            <Button onClick={() => setEditing({ type: "consent" })} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="ruc-sign-consent-btn">
              Sign consent
            </Button>
          </div>
        </div>
      ) : consent.needs_resign ? (
        <div className="border-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10 p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <ShieldCheck className="w-5 h-5 text-yellow-700 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-yellow-700 dark:text-yellow-400">Consent terms updated</div>
              <div className="text-sm font-bold mt-0.5">The auto-buy consent terms have changed since you last signed. Please re-sign.</div>
            </div>
            <Button onClick={() => setEditing({ type: "consent" })} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold">Re-sign</Button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-green-600 bg-green-50 dark:bg-green-900/10 p-3 flex items-center gap-3 flex-wrap" data-testid="ruc-consent-ok">
          <ShieldCheck className="w-5 h-5 text-green-700 dark:text-green-400 shrink-0" />
          <div className="text-xs flex-1 min-w-0">
            <div className="font-bold uppercase tracking-wider">Auto-buy consent signed by {consent.consent.signed_by_name}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
              {fmtDate(consent.consent.signed_at)} · terms {consent.consent.terms_version}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditing({ type: "consent" })} className="rounded-sm uppercase tracking-wider font-bold text-xs">Manage</Button>
        </div>
      )}

      <div className="border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/10 p-3 text-xs" data-testid="ruc-partner-status">
        <div className="font-bold uppercase tracking-wider text-blue-700 dark:text-blue-400 text-[10px]">How &quot;Buy at NZTA&quot; works</div>
        <div className="mt-1 break-words">
          Tap <span className="font-bold">Buy at NZTA</span> on any low/expired vehicle. We deep-link to NZTA&apos;s purchase portal pre-filled with the rego and km — the customer completes payment on NZTA&apos;s site (no card data touches Fieldlink). Once paid, tap <span className="font-bold">Record purchase</span> to log it so we know the new balance.
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <CountTile label="Expired" value={buckets.expired || 0} accent="bg-red-600" />
        <CountTile label="Low" value={buckets.low || 0} accent="bg-orange-500" />
        <CountTile label="Warn" value={buckets.warn || 0} accent="bg-yellow-500" />
        <CountTile label="OK" value={buckets.ok || 0} accent="bg-green-600" />
        <CountTile label="Unknown" value={buckets.unknown || 0} accent="bg-zinc-500" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{balances.length} vehicle{balances.length === 1 ? "" : "s"}</div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold text-xs">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="border-2 border-zinc-300 dark:border-zinc-700 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
            <tr>
              <th className="text-left p-2 font-bold uppercase tracking-wider">Rego</th>
              <th className="text-right p-2 font-bold uppercase tracking-wider">Current km</th>
              <th className="text-left p-2 font-bold uppercase tracking-wider">Last RUC purchase</th>
              <th className="text-right p-2 font-bold uppercase tracking-wider">Remaining</th>
              <th className="text-left p-2 font-bold uppercase tracking-wider">Auto-buy</th>
              <th className="text-right p-2 font-bold uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((b) => {
              const statusMeta = {
                expired: { cls: "bg-red-600 text-white", label: "Expired" },
                low: { cls: "bg-orange-500 text-white", label: "Low" },
                warn: { cls: "bg-yellow-500 text-white", label: "Warn" },
                ok: { cls: "bg-green-600 text-white", label: "OK" },
                unknown: { cls: "bg-zinc-500 text-white", label: "Unknown" },
              }[b.status] || { cls: "bg-zinc-500 text-white", label: "?" };
              const p = b.latest_purchase;
              return (
                <tr key={b.registration} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`ruc-row-${b.registration}`}>
                  <td className="p-2">
                    <div className="font-mono font-bold">{b.registration}</div>
                    <div className="text-[10px] text-zinc-500 break-words">{b.description}</div>
                  </td>
                  <td className="p-2 text-right font-mono">
                    {b.current_odo != null ? fmtNum(Math.round(b.current_odo)) : "—"}
                    {b.last_seen && <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{fmtDate(b.last_seen)}</div>}
                  </td>
                  <td className="p-2">
                    {p ? (
                      <div>
                        <div className="font-mono text-[11px]">{fmtNum(p.purchase_km)}km @ {fmtNum(p.purchase_odo)}km</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{p.purchase_date}{p.licence_number ? ` · ${p.licence_number}` : ""}</div>
                      </div>
                    ) : <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">None recorded</span>}
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex items-center gap-2 justify-end flex-wrap">
                      <span className="font-mono font-bold">{b.remaining_km != null ? fmtNum(Math.round(b.remaining_km)) : "—"}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${statusMeta.cls}`}>{statusMeta.label}</span>
                    </div>
                  </td>
                  <td className="p-2">
                    {b.settings?.enabled ? (
                      <span className="text-[10px] font-mono uppercase tracking-widest">
                        ≤{b.settings.threshold_km}km → +{b.settings.top_up_km}km
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Off</span>
                    )}
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openNzta(b.registration, b.settings?.top_up_km || 5000)}
                        className="rounded-sm uppercase tracking-wider font-bold text-[10px]"
                        data-testid={`ruc-buy-${b.registration}`}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" /> Buy at NZTA
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ type: "purchase", registration: b.registration, currentOdo: b.current_odo })}
                        className="rounded-sm uppercase tracking-wider font-bold text-[10px]"
                        data-testid={`ruc-record-${b.registration}`}
                      >
                        Record
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ type: "vehicle", vehicle: { registration: b.registration, manufacturer: b.description }, setting: b.settings })}
                        disabled={!consent?.signed}
                        title={!consent?.signed ? "Sign consent first" : "Configure auto-buy thresholds"}
                        className="rounded-sm uppercase tracking-wider font-bold text-[10px]"
                        data-testid={`ruc-edit-${b.registration}`}
                      >
                        Config
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent purchases */}
      {purchases.length > 0 && (
        <div className="border-2 border-zinc-300 dark:border-zinc-700 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Recent RUC purchases</div>
          <div className="space-y-1 text-xs">
            {purchases.slice(0, 10).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`ruc-purchase-${p.id}`}>
                <span className="font-mono font-bold">{p.registration}</span>
                <span className="font-mono">{fmtNum(p.purchase_km)}km @ {fmtNum(p.purchase_odo)}km</span>
                <span className="text-zinc-500">{p.purchase_date}</span>
                <span className="text-zinc-500 truncate">{p.licence_number || ""}</span>
                <span className="text-zinc-500">{p.cost_nzd != null ? `$${p.cost_nzd.toFixed(2)}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing?.type === "consent" && (
        <RucConsentDialog consent={consent} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {editing?.type === "vehicle" && (
        <RucVehicleDialog vehicle={editing.vehicle} setting={editing.setting} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {editing?.type === "purchase" && (
        <RucPurchaseDialog
          registration={editing.registration}
          currentOdo={editing.currentOdo}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CountTile({ label, value, accent }) {
  return (
    <div className={`border-2 border-zinc-900 dark:border-zinc-700 bg-white dark:bg-[#151C27] p-3`}>
      <div className={`w-2 h-2 ${accent} mb-2`} />
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl leading-none mt-1">{value}</div>
    </div>
  );
}

function RucConsentDialog({ consent, onClose, onSaved }) {
  const [name, setName] = useState(consent?.consent?.signed_by_name || "");
  const [showPad, setShowPad] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleRevoke = async () => {
    if (!window.confirm("Revoke RUC auto-buy consent? This will turn off auto-buy for every vehicle.")) return;
    setBusy(true);
    try {
      await api.delete("/ruc/consent");
      toast.success("Consent revoked.");
      onSaved?.();
    } finally { setBusy(false); }
  };

  const handleSigned = async (signature_data_url) => {
    if (!name.trim()) { toast.error("Enter your full name first."); return; }
    setBusy(true);
    try {
      await api.post("/ruc/consent", {
        signature_data_url,
        signed_by_name: name.trim(),
        terms_version: consent?.current_terms_version,
      });
      toast.success("Consent signed.");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); setShowPad(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="ruc-consent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#FF5F15]" /> Auto-buy RUC consent
          </DialogTitle>
          <DialogDescription>
            Sign once on behalf of the organisation. Covers all current and future vehicles attached to this Fieldlink account.
          </DialogDescription>
        </DialogHeader>

        <div className="border-2 border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-[#151C27] p-3 text-xs leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap" data-testid="ruc-consent-terms">
          {consent?.current_terms_text}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Terms version: {consent?.current_terms_version}
        </div>

        <div>
          <Label className="label-tag">Your full name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Sam Black" data-testid="ruc-signer-name" />
        </div>

        {showPad ? (
          <div data-testid="ruc-signature-pad">
            <SignaturePad onSave={handleSigned} onCancel={() => setShowPad(false)} testIdPrefix="ruc-sig" />
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setShowPad(true)} disabled={!name.trim() || busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="ruc-open-pad">
              <FileSignature className="w-4 h-4 mr-2" /> Sign now
            </Button>
            {consent?.signed && (
              <Button variant="outline" onClick={handleRevoke} disabled={busy} className="rounded-sm border-red-600 text-red-700 uppercase tracking-wider font-bold" data-testid="ruc-revoke-btn">
                Revoke consent
              </Button>
            )}
          </div>
        )}

        {consent?.signed && consent.consent?.signature_data_url == null && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Existing signature stored {fmtDate(consent.consent.signed_at)} by {consent.consent.signed_by_name}
            {consent.consent.ip ? ` · IP ${consent.consent.ip}` : ""}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RucVehicleDialog({ vehicle, setting, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(setting?.enabled ?? true);
  const [threshold, setThreshold] = useState(setting?.threshold_km ?? 500);
  const [topUp, setTopUp] = useState(setting?.top_up_km ?? 5000);
  const [notes, setNotes] = useState(setting?.notes || "");
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    setBusy(true);
    try {
      await api.post("/ruc/vehicle-settings", {
        registration: vehicle.registration,
        enabled,
        threshold_km: Number(threshold),
        top_up_km: Number(topUp),
        notes,
      });
      toast.success("Saved.");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await api.delete(`/ruc/vehicle-settings/${encodeURIComponent(vehicle.registration)}`);
      toast.success("Auto-buy disabled.");
      onSaved?.();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="ruc-vehicle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            Auto-buy · {vehicle.registration}
          </DialogTitle>
          <DialogDescription>
            {vehicle.manufacturer} {vehicle.model}. Settings stored now and applied automatically once a reseller API is wired.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="ruc-veh-enabled" />
            <span className="text-sm font-bold">Auto-buy RUC for this vehicle</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Trigger when ≤ (km)</Label>
              <Input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} disabled={!enabled} className="mt-2 rounded-sm border-zinc-400 font-mono" data-testid="ruc-veh-threshold" />
            </div>
            <div>
              <Label className="label-tag">Top-up amount (km)</Label>
              <Input type="number" value={topUp} onChange={(e) => setTopUp(e.target.value)} disabled={!enabled} className="mt-2 rounded-sm border-zinc-400 font-mono" data-testid="ruc-veh-topup" />
            </div>
          </div>

          <div>
            <Label className="label-tag">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Heavy diesel, RUC class H" data-testid="ruc-veh-notes" />
          </div>
        </div>

        <DialogFooter className="gap-2 flex-col sm:flex-row sm:justify-between">
          <div>
            {setting && (
              <Button variant="outline" onClick={handleDisable} disabled={busy} className="rounded-sm border-red-600 text-red-700 uppercase tracking-wider font-bold">
                Remove
              </Button>
            )}
          </div>
          <Button onClick={handleSave} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="ruc-veh-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RucPurchaseDialog({ registration, currentOdo, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [purchaseDate, setPurchaseDate] = useState(today);
  const [purchaseKm, setPurchaseKm] = useState(5000);
  const [purchaseOdo, setPurchaseOdo] = useState(currentOdo ? Math.round(currentOdo) : "");
  const [licenceNumber, setLicenceNumber] = useState("");
  const [costNzd, setCostNzd] = useState("");
  const [rucClass, setRucClass] = useState("H");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    if (!purchaseOdo || !purchaseKm) {
      toast.error("Purchase odometer and km are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/ruc/purchases", {
        registration,
        purchase_odo: Number(purchaseOdo),
        purchase_km: Number(purchaseKm),
        purchase_date: purchaseDate,
        licence_number: licenceNumber || null,
        cost_nzd: costNzd ? Number(costNzd) : null,
        ruc_class: rucClass || null,
        source: "manual",
        notes: notes || null,
      });
      toast.success("RUC purchase recorded.");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-xl" data-testid="ruc-purchase-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Record RUC purchase · {registration}</DialogTitle>
          <DialogDescription>
            Log a new RUC licence purchase. We use this + the live odometer from Cartrack to calculate remaining km.
            {currentOdo != null && <span> Current odo from Cartrack: <span className="font-mono">{Math.round(currentOdo).toLocaleString()} km</span>.</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Purchase date</Label>
            <DateField value={purchaseDate} onChange={setPurchaseDate} className="mt-2" testId="ruc-purchase-date" />
          </div>
          <div>
            <Label className="label-tag">Licence / receipt #</Label>
            <Input value={licenceNumber} onChange={(e) => setLicenceNumber(e.target.value)} className="mt-2 rounded-sm border-zinc-400 font-mono" placeholder="e.g. R1234567" data-testid="ruc-purchase-licence" />
          </div>
          <div>
            <Label className="label-tag">Odometer at purchase (km)</Label>
            <Input type="number" value={purchaseOdo} onChange={(e) => setPurchaseOdo(e.target.value)} className="mt-2 rounded-sm border-zinc-400 font-mono" data-testid="ruc-purchase-odo" />
          </div>
          <div>
            <Label className="label-tag">Km purchased</Label>
            <Input type="number" value={purchaseKm} onChange={(e) => setPurchaseKm(e.target.value)} className="mt-2 rounded-sm border-zinc-400 font-mono" data-testid="ruc-purchase-km" />
          </div>
          <div>
            <Label className="label-tag">RUC class</Label>
            <Input value={rucClass} onChange={(e) => setRucClass(e.target.value.toUpperCase())} className="mt-2 rounded-sm border-zinc-400 font-mono" placeholder="H, R, P …" data-testid="ruc-purchase-class" />
          </div>
          <div>
            <Label className="label-tag">Cost (NZD)</Label>
            <Input type="number" step="0.01" value={costNzd} onChange={(e) => setCostNzd(e.target.value)} className="mt-2 rounded-sm border-zinc-400 font-mono" placeholder="0.00" data-testid="ruc-purchase-cost" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tag">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 rounded-sm border-zinc-400" data-testid="ruc-purchase-notes" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
          <Button onClick={handleSave} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="ruc-purchase-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
